import React, { useEffect, useState } from 'react';
import { Button } from './Button';
import { useCart } from '../context/CartContext';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { CartModal } from './CartModal';
import { useNavigate } from 'react-router-dom';
import { TrainingPlanWarningModal } from './TrainingPlanWarningModal';
import { CountdownTimer } from './CountdownTimer';
import { ClientTransformationShowcase } from './ClientTransformationShowcase';
interface PlanLevel {
  id: string;
  title: string;
  emoji: string;
  shortDescription: string;
  bulletPoints: string[];
  fullDescription: string;
  priceOneMonth: number;
  priceThreeMonths: number;
}
interface TrainingPlansSectionProps {
  showProgressSection?: boolean;
}
export const TrainingPlansSection: React.FC<TrainingPlansSectionProps> = ({
  showProgressSection = true
}) => {
  const {
    addItem,
    cartModalOpen,
    setCartModalOpen,
    lastAddedItem,
    trainingPlanWarningOpen,
    setTrainingPlanWarningOpen,
    existingTrainingPlanName
  } = useCart();
  const navigate = useNavigate();
  const [expandedPlans, setExpandedPlans] = useState<Record<string, boolean>>({});
  const [selectedDurations, setSelectedDurations] = useState<Record<string, '1' | '3'>>({
    beginner: '3',
    intermediate: '3',
    advanced: '3'
  });
  const togglePlanDetails = (planId: string) => {
    setExpandedPlans(prev => ({
      ...prev,
      [planId]: !prev[planId]
    }));
  };
  const handleDurationChange = (planId: string, duration: '1' | '3') => {
    setSelectedDurations(prev => ({
      ...prev,
      [planId]: duration
    }));
  };
  const handleAddToCart = (plan: PlanLevel) => {
    const duration = selectedDurations[plan.id];
    const price = duration === '1' ? plan.priceOneMonth : plan.priceThreeMonths;
    addItem({
      id: `training-${plan.id}-${duration}`,
      name: `Tréninkový plán - ${plan.title} (${duration === '1' ? '1 měsíc' : '3 měsíce'})`,
      price,
      type: 'training',
      details: `${plan.title}, ${duration === '1' ? '1 měsíc' : '3 měsíce'}`
    });
    // Immediately navigate to cart after adding item
    navigate('/cart');
    // Scroll to top will happen automatically in Cart component
  };
  const handleContinueShopping = () => {
    setCartModalOpen(false);
  };
  const handleGoToCart = () => {
    setCartModalOpen(false);
    navigate('/cart');
    // Scroll to top of the page will happen automatically due to useEffect in Cart component
  };
  const scrollToSuperfoods = () => {
    navigate('/superpotraviny');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };
  const plans: PlanLevel[] = [{
    id: 'beginner',
    title: 'ZAČÁTEČNÍK',
    emoji: '💪',
    shortDescription: 'Jsi úplně na začátku, a nevíš jak cvičit správně? Tenhle plán je přesně pro tebe! Ukážu ti přesně:',
    bulletPoints: ['Ke každému cviku dostaneš video se správnou technikou i tempem, aby byl trénink 100% efektivní.', 'Ukážu ti, jak naplno využít tréninkový plán se 4 tréninky a 3 dny odpočinku.', 'jak správně cvičit technicky, aby ses vyhnul zraněním', 'přehledný systém rozdělený podle tréninkových dnů, svalových partií a dnů na odpočinek', 'kolik opakování a sérií máš přesně dělat v každém tréninku', 'kdy a jak dlouho správně odpočívat mezi sériemi i tréninky, pro docílení nejlepších výsledků', 'využití tzv. začátečnického potenciálu – nejrychlejší výsledky v prvních měsících cvičení', 'plán nastavený tak, aby sis nepřetrénoval/a a svaly měly prostor růst', 'jasné vedení krok po kroku – od prvního tréninku až po budování správných návyků'],
    fullDescription: 'Celý plán je navržený tak, aby si co nejlépe využil tzv. začátečníký potenciál – tedy období, kdy tělo roste nejrychleji a výsledky jdou vidět téměř okamžitě. Zároveň ale dbáme na to, abys ses nepřetrénoval/a – svaly potřebují čas na regeneraci. A právě regeneraci plán zohledňuje, tak aby sis mohl/a budovat formu bezpečně a efektivně. Nemusíš se ničeho bát – všechno ti ukážeme krok po kroku. Stačí nabrat odvahu a konečně se rozhodnout se změnit jak fyzicky, tak psychicky. Možná právě v tomhle tréninkovém plánu, se skrývá klíč k sebevědomí a změně sebe, po které toužíš. Stačí se rozhodnout investovat do sebe, udělat první krok – jakmile uvidíš reálné výsledky pak už tě nic nezastaví. Stačí začít!',
    priceOneMonth: 899,
    priceThreeMonths: 1799
  }, {
    id: 'intermediate',
    title: 'POKROČILÝ',
    emoji: '🔥',
    shortDescription: 'Cvičíš už delší dobu, máš výsledky, ale cítíš, že nevidíš výsledky jaké by sis přál? Máš motivaci, ale už nevíš, jak dál posouvat svoji formu? Tenhle plán je přesně pro tebe! Ukážu ti přesně:',
    bulletPoints: ['Ke každému cviku dostaneš video se správnou technikou i tempem, aby byl trénink 100% efektivní.', 'Ukážu ti, jak naplno využít tréninkový plán s 5 tréninky a 2 dny odpočinku.', 'jak správně nastavit progresivní přetížení (progressive overload)', 'Tréninkový plán rozvržený tak, aby přinesl co nejrychlejší a nejefektivnější výsledky', 'přesné počty opakování a sérií pro každou svalovou partii', 'cyklování tréninku, aby tělo nepřestalo reagovat a výsledky se nezastavily', 'jak cvičit tvrdě, ale chytře – bez přetrénování', 'plán vycházející z ověřených studií a podpořený osobní zkušeností', 'důraz na regeneraci a správnou rovnováhu mezi výkonem a odpočinkem'],
    fullDescription: 'Celý plán je navržený tak, aby tě dostal z bodu mrazu nebo pomalých výsledků zpátky do růstu.\n\nPlán zahrnuje jak moje zkušenosti, tak výsledky tisíců lidí, a dohromady tvoří plán, který je přímo dělaný pro lidi, kteří chcou znovu vidět progress.\n\nVyužívá osvědčené metody jako progresivní přetížení, odzkoušený split a cyklení tréninků – tak, aby tvoje tělo opět začalo reagovat.\n\nZároveň zohledňuje, že už nejsi začátečník – víš, jak cvičit, ale potřebuješ jasný směr, co a jak dělat, jelikož i "malá" věc, která se říká progressive overload, může začít dělat výsledky, které by jsi nikdy nečekal, že jich můžeš dosánout.\n\nVšechno je sestavené tak, aby ses mohl posouvat dál tvrdě, ale chytře. Bez zbytečného přetrénování, ztráty času a peníz.\n\nStačí se rozhodnout, a uvěřit ve fakt, že i malá změna v tréninku, může vést k výsledkům, po kterých toužíš. Třeba přesně tahla informace, o které nevíš, se může nacházet v tomhle tréninkovém plánu a jsi jenom jeden krůček od toho, aby si viděl výsledky po kterých toužíš. Stačí začít!',
    priceOneMonth: 999,
    priceThreeMonths: 1899
  }, {
    id: 'advanced',
    title: 'PROFESIONÁL',
    emoji: '🏆',
    shortDescription: 'Cvičíš roky. Ale přijde ti, že už nevidíš výsledky jako na začátku? Možná právě to, co přesně potřebuješ pro tvůj požadovaný růst, se nachází tady – v tomhle tréninkovém plánu. Můžeš zjistit, že některé věci, které už delší dobu děláš, nejsou úplně správně – a právě to tě může brzdit od formy, po kterou toužíš. Tenhle plán ti ukáže:',
    bulletPoints: ['Ke každému cviku dostaneš video se správnou technikou i tempem, aby byl trénink 100% efektivní.', 'Ukážu ti, jak naplno využít tréninkový plán s 6 tréninky a 1 dnem na odpočinek.', 'optimalizovaný split s důrazem na dokonalou regeneraci vysoký objem + vysoká intenzita ve správném poměru', 'systém sledování progresu a postupného navyšování výkonu', 'detailní popis správné techniky cviků a tempa pro růst svalů', 'správné rozestupy mezi tréninky jednotlivých partií – pro ideální regeneraci a maximální výsledky', 'správná délka pauz mezi sériemi – podle studií zásadně ovlivňuje růst svalů (včetně mých vlastních zkušeností)', 'optimální počet pracovních sérií a rozsah opakování pro každou svalovou partii', 'strategicky postavený plán navržený na základě toho, co opravdu funguje'],
    fullDescription: 'Tenhle plán není pro každého. Ale pokud to myslíš vážně a chceš konečně vytěžit ze svého tréninku maximum – tohle může být přesně to, co jsi celou dobu hledal.\n\nNejde jen o náhodné rozpisy. Je to strategicky postavený plán – navržený na základě toho, co opravdu funguje.\n\nA pokud všechny tyhle prvky začneš dělat správně tak, jak tělo skutečně potřebuje pro maximální růst – můžeš i po několika létech stagnace a promarněného času, konečně zažít požadovanou fyzickou změnu těla, kterou hledáš.\n\nTvému tělu při tréninku možná chybí, jedna informace, která může udělat větší změnu, než si dokážeš představit. Představ si, že teď tu informaci máš na dosah, a je jen na tobě, jestli se rozhodneš ji získat anebo budeš marnit svůj čas a peníze v posilovně nadále.',
    priceOneMonth: 1099,
    priceThreeMonths: 1999
  }];
  return <section className="section section-dark relative py-16" id="training" data-id="element-1181">
      <CartModal isOpen={cartModalOpen} onClose={handleContinueShopping} onGoToCart={handleGoToCart} onContinueShopping={handleContinueShopping} productName={lastAddedItem || ''} data-id="element-1182" />
      <TrainingPlanWarningModal isOpen={trainingPlanWarningOpen} onClose={() => setTrainingPlanWarningOpen(false)} existingPlanName={existingTrainingPlanName || ''} data-id="element-1183" />
      <div className="container mx-auto px-4 section-content" data-id="element-1184">
        <motion.h2 initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} viewport={{
        once: true
      }} className="text-3xl md:text-6xl font-black mb-8 md:mb-12 text-center section-header-accent" data-id="element-1185">
          <span className="highlight-text" data-id="element-1186">CENÍK</span> TRÉNINKOVÝCH PLÁNŮ
        </motion.h2>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} viewport={{
        once: true
      }} className="mb-8 md:mb-10" data-id="element-1187">
          <div className="relative max-w-4xl mx-auto" data-id="element-1188">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bJc9RS4VcfAJGBi69kEcpr/791f6e22-5e9c-4f05-a0eb-997119d00581.png" alt="Aplikace Trainerize na mobilních telefonech" className="w-full h-auto object-contain rounded-xl shadow-2xl" data-id="element-1189" />
            <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent rounded-xl flex items-end" data-id="element-1190">
              <div className="p-4 md:p-6" data-id="element-1191">
                <h3 className="text-xl md:text-2xl font-bold mb-1 md:mb-2 text-glow" data-id="element-1192">
                  Přehledná aplikace pro tvůj trénink
                </h3>
              </div>
            </div>
          </div>
        </motion.div>
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} viewport={{
        once: true
      }} className="max-w-4xl mx-auto mb-8 md:mb-12 text-center" data-id="element-1193"></motion.div>
        <div className="mb-4 md:mb-6 text-center" data-id="element-1194">
          <h3 className="text-xl md:text-2xl font-bold" id="plan-cards" data-id="element-1195">
            Vyber si svůj plán podle úrovně:
          </h3>
        </div>

        <CountdownTimer data-id="element-1196" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 training-plans-cards" data-id="element-1197">
          {plans.map((plan, index) => <motion.div key={plan.id} className="premium-card flex flex-col h-full bg-gray-900/80 border border-gray-800" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.2
        }} viewport={{
          once: true
        }} whileHover={{
          y: -5,
          boxShadow: '0 20px 40px -5px rgba(34, 197, 94, 0.3)',
          borderColor: 'rgba(34, 197, 94, 0.3)'
        }} data-id="element-1198">
              {/* Header with title */}
              <div className="p-4 md:p-5 border-b border-gray-800 relative" data-id="element-1199">
                <div className="flex items-center justify-between" data-id="element-1200">
                  <h3 className="text-lg md:text-xl font-black text-green-500 flex items-center" data-id="element-1201">
                    {plan.title}
                  </h3>
                  {plan.id === 'intermediate' && <div className="relative ml-3" data-id="element-1202">
                      <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-500 rounded-full blur-sm opacity-60 animate-pulse" data-id="element-1203"></div>
                      <div className="relative bg-gradient-to-r from-green-600 to-green-500 text-white text-[9px] md:text-[11px] font-black px-2.5 md:px-3 py-1 md:py-1.5 rounded-full shadow-lg border border-white/30" data-id="element-1204">
                        <span className="relative z-10 tracking-wider whitespace-nowrap" data-id="element-1205">
                          NEJPRODÁVANĚJŠÍ
                        </span>
                      </div>
                    </div>}
                </div>
              </div>
              {/* Content */}
              <div className="p-4 md:p-5 flex-1 flex flex-col" data-id="element-1206">
                <p className="text-gray-300 text-xs md:text-sm mb-3" data-id="element-1207">
                  {plan.shortDescription}
                </p>
                <div className="flex-1 flex flex-col" data-id="element-1208">
                  <ul className="space-y-1 mb-4 flex-1" data-id="element-1209">
                    {plan.bulletPoints.map((point, idx) => <li key={idx} className="flex items-start" data-id="element-1210">
                        <span className="text-green-500 mr-2 mt-0.5 text-xs" data-id="element-1211">
                          •
                        </span>
                        <span className={`text-gray-300 text-xs ${plan.id === 'intermediate' ? 'leading-relaxed' : ''}`} data-id="element-1212">
                          {point}
                        </span>
                      </li>)}
                  </ul>
                  <motion.button onClick={() => togglePlanDetails(plan.id)} className="flex items-center text-green-500 hover:text-green-400 transition-colors text-xs font-bold mb-4" whileHover={{
                x: 5
              }} transition={{
                duration: 0.2
              }} data-id="element-1213">
                    {expandedPlans[plan.id] ? <>
                        <span data-id="element-1214">MÉNĚ INFORMACÍ</span>
                        <ChevronUpIcon className="w-4 h-4 ml-1" data-id="element-1215" />
                      </> : <>
                        <span data-id="element-1216">VÍCE INFORMACÍ</span>
                        <ChevronDownIcon className="w-4 h-4 ml-1" data-id="element-1217" />
                      </>}
                  </motion.button>
                  <motion.div initial={false} animate={{
                height: expandedPlans[plan.id] ? 'auto' : 0,
                opacity: expandedPlans[plan.id] ? 1 : 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-1218">
                    {expandedPlans[plan.id] && <div className="mt-2 text-gray-300 bg-gray-800 p-4 rounded-lg text-xs mb-4" data-id="element-1219">
                        <p className="whitespace-pre-line" data-id="element-1220">
                          {plan.fullDescription}
                        </p>
                      </div>}
                  </motion.div>
                  {/* Duration selection tabs */}
                  <div className="flex justify-center mb-4 bg-gray-800 rounded-md overflow-hidden" data-id="element-1221">
                    <button onClick={() => handleDurationChange(plan.id, '1')} className={`flex-1 py-3 text-center font-bold transition-colors text-xs ${selectedDurations[plan.id] === '1' ? 'bg-gray-700 text-white' : 'bg-transparent text-gray-400 hover:bg-gray-700/50'}`} data-id="element-1222">
                      1 měsíc
                    </button>
                    <button onClick={() => handleDurationChange(plan.id, '3')} className={`flex-1 py-3 text-center font-bold transition-colors text-xs ${selectedDurations[plan.id] === '3' ? 'bg-green-600 text-white' : 'bg-transparent text-gray-400 hover:bg-gray-700/50'}`} data-id="element-1223">
                      3 měsíce
                    </button>
                  </div>
                  {/* Selected price display */}
                  <div className="text-center mb-4 md:mb-6" data-id="element-1224">
                    <div className="text-2xl md:text-4xl font-black text-green-500 mb-2 drop-shadow-[0_0_10px_rgba(34,197,94,0.3)]" data-id="element-1225">
                      {selectedDurations[plan.id] === '1' ? `${plan.priceOneMonth} Kč` : `${plan.priceThreeMonths} Kč`}
                    </div>
                    {selectedDurations[plan.id] === '3' && <div className="relative inline-block bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/20 rounded-lg px-4 py-2 mb-2 backdrop-blur-sm" data-id="element-1226">
                        <div className="text-xs md:text-sm text-gray-300 flex items-center justify-center gap-2" data-id="element-1227">
                          <span className="text-gray-500 line-through decoration-red-500/40 decoration-1" data-id="element-1228">
                            {plan.priceOneMonth * 3} Kč
                          </span>
                          <span className="font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-300 uppercase tracking-wide" data-id="element-1229">
                            Ušetříš{' '}
                            {plan.priceOneMonth * 3 - plan.priceThreeMonths} Kč
                          </span>
                        </div>
                      </div>}
                    <div className="flex items-center justify-center gap-1.5 text-xs md:text-sm text-gray-400 font-medium tracking-wide mt-1" data-id="element-1230">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-500/50" data-id="element-1231"></div>
                      Jednorázová platba
                    </div>
                  </div>
                  {/* Add to cart button */}
                  <button onClick={() => handleAddToCart(plan)} className="bg-green-600 text-white py-3 md:py-4 rounded-md font-bold flex items-center justify-center hover:bg-green-700 transition-colors text-xs md:text-base" data-id="element-1232">
                    <CheckIcon className="w-4 h-4 md:w-5 md:h-5 mr-2" data-id="element-1233" />
                    PŘIDAT DO KOŠÍKU
                  </button>
                </div>
              </div>
            </motion.div>)}
        </div>
        <div className="mt-8 md:mt-12" data-id="element-1234">
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-6 md:mb-8" data-id="element-1235">
            <div className="flex items-center" data-id="element-1236">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1237" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-1238">
                Přehledná aplikace
              </span>
            </div>
            <div className="flex items-center" data-id="element-1239">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1240" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-1241">
                Jednorázová platba
              </span>
            </div>
            <div className="flex items-center" data-id="element-1242">
              <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2" data-id="element-1243" />
              <span className="text-gray-300 text-xs md:text-base" data-id="element-1244">
                Získáš okamžitý přístup do emailu
              </span>
            </div>
          </div>

          <ClientTransformationShowcase data-id="element-1245" />

          {showProgressSection && <motion.div initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} viewport={{
          once: true,
          margin: '-100px'
        }} className="relative max-w-5xl mx-auto mt-16 md:mt-24" data-id="element-1246">
              {/* Decorative background glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-green-600/20 via-green-400/10 to-green-600/20 rounded-2xl blur-2xl opacity-50 pointer-events-none" data-id="element-1247" />

              <div className="relative bg-gray-900/90 backdrop-blur-xl border border-green-500/30 rounded-2xl p-8 md:p-16 text-center overflow-hidden shadow-2xl" data-id="element-1248">
                {/* Elegant background pattern/accent */}
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-500 to-transparent opacity-50" data-id="element-1249" />
                <div className="absolute -top-24 -right-24 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-1250" />
                <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-1251" />

                <div className="relative z-10 flex flex-col items-center" data-id="element-1252">
                  <motion.span initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.2,
                duration: 0.6
              }} className="text-green-500 font-bold tracking-[0.2em] uppercase text-xs md:text-sm mb-4" data-id="element-1253">
                    Exkluzivní nabídka
                  </motion.span>

                  <motion.h3 initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.3,
                duration: 0.6
              }} className="text-2xl md:text-4xl lg:text-5xl font-black text-white mb-2 leading-tight" data-id="element-1254">
                    Chceš aby tvůj progress byl{' '}
                    <br className="hidden md:block" data-id="element-1255" />
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-gray-200 to-gray-400" data-id="element-1256">
                      ještě rychlejší?
                    </span>
                  </motion.h3>

                  <motion.p initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: 0.4,
                duration: 0.6
              }} className="text-xl md:text-3xl font-bold mt-4 mb-10 text-glow" data-id="element-1257">
                    <span className="highlight-text" data-id="element-1258">Mám pro tebe řešení!</span>
                  </motion.p>

                  <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} whileInView={{
                opacity: 1,
                scale: 1
              }} whileHover={{
                scale: 1.05
              }} transition={{
                delay: 0.5,
                duration: 0.4,
                type: 'spring',
                stiffness: 200
              }} data-id="element-1259">
                    <Button variant="primary" size="lg" onClick={scrollToSuperfoods} className="luxury-button text-base md:text-xl px-10 py-6 shadow-[0_0_30px_-5px_rgba(34,197,94,0.4)] hover:shadow-[0_0_50px_-10px_rgba(34,197,94,0.6)] border border-green-400/30" data-id="element-1260">
                      CHCI RYCHLEJŠÍ PROGRESS!
                    </Button>
                  </motion.div>

                  <motion.p initial={{
                opacity: 0
              }} whileInView={{
                opacity: 1
              }} transition={{
                delay: 0.7,
                duration: 0.6
              }} className="mt-6 text-gray-400 text-xs md:text-sm max-w-lg mx-auto" data-id="element-1261">
                    Objev tajemství, díky kterému jsem měl výsledky rychleji než
                    kamarádi, se kterými jsem začínal.
                  </motion.p>
                </div>
              </div>
            </motion.div>}
        </div>
      </div>
      <div className="section-divider divider-slant-up" data-id="element-1262"></div>
    </section>;
};